import { defineStore } from 'pinia';
import {
  computed,
  ref,
  useCookie,
  useFetch,
  useRuntimeConfig,
  navigateTo,
  nextTick,
  type Ref,
} from '#imports';
import {
  createAuthSessionController,
  asAuthUser,
  authUserHeaders,
  runLogoutFlow,
  usePermissionChecker,
  hasRole,
  hasMatch,
  getAuthCookieOptions,
} from 'r3ui-lib';
import type { AuthUserDto } from '~/types/ports/auth.api';
import {
  isUnauthorizedError,
  presentPageLoadError,
} from '~/utils/presentPageLoadError';

export const useAuthStore = defineStore(
  'auth',
  () => {
    const config = useRuntimeConfig();
    const { authApiUrl, origin } = config.public;
    // Опции обязаны совпадать с теми, с которыми cookie ставит серверный
    // callback: без domain удаление не снимет cookie родительского домена.
    const cookie = useCookie<string | null>(
      'auth_token',
      getAuthCookieOptions({
        origin: origin as string | undefined,
        authCookieDomain: config.public.authCookieDomain as string | undefined,
      })
    ) as Ref<string | null>;
    const token = ref<string | null | undefined>(cookie.value);
    const user = ref<AuthUserDto | null>(null);

    const isAuthenticated = computed(() => Boolean(token.value));

    const hasUserPermission = usePermissionChecker(
      computed(() => user.value?.all_permissions ?? [])
    );

    const userRoleNames = computed(() => {
      if (!Array.isArray(user.value?.roles)) {
        return [];
      }

      return user.value.roles
        .map(role => role?.name)
        .filter((roleName): roleName is string => Boolean(roleName));
    });

    const hasUserRole = (role: string | string[]) => {
      if (Array.isArray(role)) {
        return hasMatch(role, userRoleNames.value);
      }

      return hasRole(userRoleNames.value, role);
    };

    const session = createAuthSessionController({
      token,
      user,
      async loadUser(requestedToken) {
        const { data, error } = await useFetch<AuthUserDto>(
          '/api/services/auth/user',
          {
            headers: authUserHeaders(requestedToken),
          }
        );
        if (error.value) throw error.value;
        const loaded = asAuthUser<AuthUserDto>(data.value);
        if (!loaded) throw new Error('fetchUser: пустой ответ');
        return loaded;
      },
      isUnauthorized: isUnauthorizedError,
      onError: presentPageLoadError,
      clearCookie: () => {
        cookie.value = null;
      },
      runLogout,
    });
    const { fetchUser, logout } = session;

    function resetStorage() {
      ['breadcrumbs', 'filters', 'tables'].forEach(key => {
        localStorage.removeItem(key);
      });
    }

    async function logOutSSO() {
      return $fetch('/api/auth/logout', { method: 'POST' });
    }

    async function runLogout(resetSession: () => void) {
      const baseLogoutUrl = authApiUrl ? `${authApiUrl}/logout` : '/login';
      const redirectPath = origin
        ? `${baseLogoutUrl}?redirect=${encodeURIComponent(origin)}`
        : baseLogoutUrl;
      const navigateWithExternal = async (url: string) => {
        if (/^https?:\/\//.test(url)) {
          await navigateTo(url, { external: true });
          return;
        }
        await navigateTo(url);
      };
      await runLogoutFlow({
        logOutSSO,
        nextTick,
        navigateTo: navigateWithExternal,
        onReset: () => {
          resetSession();
          resetStorage();
        },
        onError: (error: unknown) => {
          // eslint-disable-next-line no-console
          console.error('[logout]', error);
        },
        redirectPath,
      });
    }

    return {
      isAuthenticated,
      logout,
      fetchUser,
      token,
      user,
      hasUserPermission,
      hasUserRole,
    };
  },
  {
    persist: {
      pick: ['user'],
    },
  }
);
